import { useMemo } from "react";
import { UIFlightData } from "@flights/types";
import useRouteName from "hooks/useRouteName";
import { useSelector } from "react-redux";
import { getAncillaries, getFlightDetailsFetchStatus } from "store/flightDetails/selectors";
import { useI18n, t } from "@bookingcom/lingojs-react";
import {
  priceBreakdownUtils,
  localizationUtils,
  isFeatureRunningClientSide,
  ryanairUtils
} from "@flights/web-api-utils-universal";
import { useFlightOfferWithDiscount } from "hooks/useFlightOfferWithDiscount";
import usePointOfSale from "hooks/usePointOfSale";
import { getExtraProducts, getPreOptedAncillaries } from "ancillaries/store/extraProducts/selectors";
import { transformPreOptedExtras } from "./transforms/preOptedExtras";
import uniq from "lodash/uniq";
import { transformDisclaimersForExtras, transformDisclaimersForPriceBreakdown } from "./transforms/disclaimers";
import { type BreakdownProps } from "./Breakdown/Breakdown";

export type UnifiedPriceBreakdownFlightCardPriceControllerProps = {
  flight?: UIFlightData;
};

type CopyFuncParams = Parameters<typeof priceBreakdownUtils.getExtraCopy>;
type POSType = CopyFuncParams[3]["pointOfSale"];

export function useUnifiedPriceBreakdownFlightCardPriceController(
  props: UnifiedPriceBreakdownFlightCardPriceControllerProps
) {
  const { flight } = props;
  const i18n = useI18n();
  const ipCountry = usePointOfSale();
  const routeName = useRouteName();
  const ancillaries = useSelector(getAncillaries);
  const extraProducts = useSelector(getExtraProducts);
  const preOptedAncillaries = useSelector(getPreOptedAncillaries);
  const fetchStatus = useSelector(getFlightDetailsFetchStatus);
  const segments = useMemo(() => flight?.segments, [flight]);
  const isHungaryDepartingRyanAir = useMemo(
    () => localizationUtils.isHungaryDepartingRyanAirFlight(segments),
    [segments]
  );
  const baseOptions = useMemo(
    () => ({
      pointOfSale: (ipCountry || "") as POSType,
      isFlightFromRyanAirHungary: isHungaryDepartingRyanAir,
      segments: segments || []
    }),
    [ipCountry, isHungaryDepartingRyanAir, segments]
  );
  const unifiedPriceBreakdown = useMemo(() => flight?.unifiedPriceBreakdown, [flight]);

  const allExtraProducts = useMemo(() => {
    return isFeatureRunningClientSide("FLIGHTS_PRICING_UNIFIED_PRICE_BREAKDOWN_USE_EXTRA_PRODUCTS")
      ? extraProducts
      : undefined;
  }, [extraProducts]);

  const allAncillaries = useMemo(() => {
    return allExtraProducts || ancillaries || flight?.ancillaries || {};
  }, [flight, ancillaries, allExtraProducts]);

  const preOptedExtras = useMemo(
    () => transformPreOptedExtras(preOptedAncillaries, allAncillaries),
    [preOptedAncillaries, allAncillaries]
  );

  const addedItems = useMemo(() => {
    // show pre-opted ancillaries only in flight details
    if (!routeName.includes("flightdetails")) return [];

    return priceBreakdownUtils.transformUnifiedPriceBreakdownItemForExtra(i18n, preOptedExtras, baseOptions);
  }, [routeName, i18n, baseOptions, preOptedExtras]);

  const isFlightDetailsLoading = useMemo(() => {
    if (!routeName.includes("flightdetails")) return false;
    return fetchStatus === "loading";
  }, [fetchStatus, routeName]);

  const flightDetailsLoadingText = useMemo(() => {
    return i18n.trans(t("flights_price_breakdown_loading_details"));
  }, [i18n]);

  const disclaimers = useMemo(() => {
    return uniq([
      ...transformDisclaimersForPriceBreakdown(flight?.priceBreakdown),
      ...transformDisclaimersForExtras(preOptedExtras)
    ]);
  }, [flight, preOptedExtras]);

  const { isWithDiscount } = useFlightOfferWithDiscount(flight); // flights_web_unified_price_breakdown

  const isRyanair = useMemo(() => ryanairUtils.isRyanairSegments(segments), [segments]);

  const expandedByDefault = useMemo<BreakdownProps["expandedByDefault"]>(() => {
    return isRyanair
      ? {
          FLIGHT_ADULT: true,
          FLIGHT_CHILD: true,
          FLIGHT_INFANT: true,
          EXTRA: true
        }
      : undefined;
  }, [isRyanair]);

  return {
    unifiedPriceBreakdown,
    isHungaryDepartingRyanAir,
    isFlightDetailsLoading,
    expandedByDefault,
    disclaimers,
    isWithDiscount, // flights_web_unified_price_breakdown
    flightDetailsLoadingText,
    routeName,
    addedItems
  };
}
